<script>
  import { scaleLinear, scaleLog } from "d3-scale";
  import { extent } from "d3-array";
  import { format } from "d3-format";
  import { regressionLinear } from "d3-regression";
  import Bubble from "./Bubble.svelte";
  import Label from "./Label.svelte";
  import Grid from "./Grid.svelte";
  import Tooltip from "./Tooltip.svelte";
  import NoDataAvailable from "./NoDataAvailable.svelte";
  import Loading from "./Loading.svelte";
  import { displayData, noData } from "./../stores";
  import { colors } from "./../colors";

  export let scatterData;
  export let xAxisTitle;
  export let xMin;
  export let xMax;
  export let xLog;
  export let yAxisTitle;
  export let yMin;
  export let yMax;
  export let yLog;
  export let dotRadius;
  export let dotOpacity;
  export let dotColor;
  export let chartTitle;
  export let xReference;
  export let yReference;
  export let primaryCountry;
  export let referenceCountries;
  export let highlightGroup;
  export let xVariable;
  export let yVariable;
  export let scatterLoading;
  export let regressionLine;

  const margin = { top: 40, bottom: 50, left: 50, right: 20 };
  const width = 800;
  const height = 500;
  const innerHeight = height - margin.top - margin.bottom;
  const innerWidth = width - margin.left - margin.right;

  let showTooltip = false;
  let tooltipContent;
  let tooltipX;
  let tooltipY;

  $: minX = xMin || extent(scatterData, (d) => d.x)[0];
  $: maxX = xMax || extent(scatterData, (d) => d.x)[1];
  $: xScale = xLog
    ? scaleLog().domain([minX, maxX]).range([0, innerWidth])
    : scaleLinear().domain([minX, maxX]).range([0, innerWidth]);
  $: xTicks = xLog
    ? xScale
        .ticks()
        .filter(
          (d) =>
            Number.isInteger(Math.log10(d)) ||
            Number.isInteger(Math.log10(d * 2))
        )
    : xScale.ticks(5);

  $: minY = yMin || extent(scatterData, (d) => d.y)[0];
  $: maxY = yMax || extent(scatterData, (d) => d.y)[1];
  $: yScale = yLog
    ? scaleLog().domain([minY, maxY]).range([innerHeight, 0])
    : scaleLinear().domain([minY, maxY]).range([innerHeight, 0]);
  $: yTicks = yLog
    ? yScale
        .ticks()
        .filter(
          (d) =>
            Number.isInteger(Math.log10(d)) ||
            Number.isInteger(Math.log10(d * 2))
        )
    : yScale.ticks(5);

  $: highlightData = scatterData
    .filter((d) => {
      if (highlightGroup.group === "Income group") {
        return d.income === highlightGroup.label;
      }
      if (highlightGroup.group === "Region") {
        return d.wbregion === highlightGroup.label;
      }
      if (highlightGroup.group === "Fragile") {
        return d.fragile === highlightGroup.label;
      }
      if (highlightGroup.group === "Lending") {
        return d.lending === highlightGroup.label;
      }
    })
    .map((d) => d.label);

  $: regression = regressionLinear()
    .x((d) => xScale(d.x))
    .y((d) => yScale(d.y))
</script>

{#if scatterLoading}
  <Loading width={innerWidth} height={innerHeight} />
{:else}
  <svg {width} {height} class="chart">
    <g transform={`translate(${margin.left},${margin.top})`}>
      <text x={0} y={-20} font-size={24}>{chartTitle}</text>

      {#if !scatterLoading && $noData}
        <NoDataAvailable width={innerWidth} height={innerHeight} />
      {/if}

      <Grid
        gridType="xGrid"
        {innerHeight}
        {innerWidth}
        scale={xScale}
        ticks={xTicks}
        numberFormat=".2s"
      />
      <Grid
        gridType="yGrid"
        {innerHeight}
        {innerWidth}
        scale={yScale}
        ticks={yTicks}
        numberFormat=".1s"
      />

      {#if xReference}
        <line
          x1={xScale(xReference)}
          x2={xScale(xReference)}
          y1={0}
          y2={innerHeight}
          stroke-width={1}
          stroke={"#555555"}
        />
      {/if}

      {#if yReference}
        <line
          y1={yScale(yReference)}
          y2={yScale(yReference)}
          x1={0}
          x2={innerWidth}
          stroke-width={1}
          stroke={"#555555"}
        />
      {/if}

      <text
        transform={`translate(${-30},${innerHeight / 2}) rotate(-90)`}
        text-anchor={"middle"}>{yAxisTitle}</text
      >
      <text x={innerWidth / 2} y={innerHeight + 35} text-anchor={"middle"}
        >{xAxisTitle}</text
      >

      {#if $displayData}
        {#each scatterData as d}
          <Bubble
            cx={xScale(d.x)}
            cy={yScale(d.y)}
            fill={highlightData.includes(d.label)
              ? dotColor
              : primaryCountry && primaryCountry.label === d.label
              ? colors.primary
              : referenceCountries !== null &&
                referenceCountries.map((d) => d.label).includes(d.label)
              ? colors.reference
              : "#cccccc"}
            opacity={dotOpacity}
            r={dotRadius}
            ttContent={`<p class="countryname">${d.label}</p>
        <p>${xVariable.label}: <span class="value">${format(",.1f")(
              d.x
            )}</span></p>
        <p>${yVariable.label}: <span class="value">${format(",.1f")(
              d.y
            )}</span></p>`}
            bind:tooltipContent
            bind:tooltipX
            bind:tooltipY
            bind:showTooltip
          />
        {/each}
        {#if regressionLine}
          <line
            x1={regression(scatterData)[0][0]}
            x2={regression(scatterData)[1][0]}
            y1={regression(scatterData)[0][1]}
            y2={regression(scatterData)[1][1]}
            stroke="black"
            stroke-width={2}
          />
        {/if}
      {/if}

      {#if referenceCountries}
        {#each referenceCountries as refCountry}
          <circle
            cx={xScale(refCountry.x)}
            cy={yScale(refCountry.y)}
            r={dotRadius + 5}
            fill-opacity={0}
            stroke-width={4}
            stroke={"#ffffff"}
            class="no-pointer-events"
          />
          <circle
            cx={xScale(refCountry.x)}
            cy={yScale(refCountry.y)}
            r={dotRadius + 5}
            fill-opacity={0}
            stroke-width={1.5}
            stroke={colors.reference}
            class="no-pointer-events"
          />
        {/each}
      {/if}

      {#if primaryCountry && primaryCountry.x && primaryCountry.y}
        <circle
          cx={xScale(primaryCountry.x)}
          cy={yScale(primaryCountry.y)}
          r={dotRadius + 5}
          fill-opacity={0}
          stroke-width={4}
          stroke={"#ffffff"}
          class="no-pointer-events"
        />
        <circle
          cx={xScale(primaryCountry.x)}
          cy={yScale(primaryCountry.y)}
          r={dotRadius + 5}
          fill-opacity={0}
          stroke-width={1.5}
          stroke={colors.primary}
          class="no-pointer-events"
        />
      {/if}

      {#if referenceCountries}
        {#each referenceCountries as refCountry}
          <Label
            x={xScale(refCountry.x)}
            y={yScale(refCountry.y)}
            fill={colors.reference}
            text={refCountry.label}
            font-size={10}
            textAnchor="middle"
            lineLabel={false}
          />
        {/each}
      {/if}

      {#if primaryCountry && primaryCountry.x && primaryCountry.y}
        <Label
          x={xScale(primaryCountry.x)}
          y={yScale(primaryCountry.y)}
          fill={colors.primary}
          text={primaryCountry.label}
          font-size={12}
          textAnchor="middle"
          lineLabel={false}
        />
      {/if}
    </g>
  </svg>
  {#if showTooltip}
    <Tooltip {tooltipX} {tooltipY} {tooltipContent} />
  {/if}
{/if}

<style>
  .no-pointer-events {
    pointer-events: none;
  }
</style>
